import i18n from './index';

const COMMON_PREFIX = 'common:';

function withNs(key) {
  if (!key) return `${COMMON_PREFIX}unexpectedError`;
  return key.includes(':') ? key : `${COMMON_PREFIX}${key}`;
}

const KNOWN_DETAIL = [
  [/a license already exists for this process/i, 'Já existe uma licença vinculada a este processo.'],
  [/license not found/i, 'Licença não encontrada.'],
];

function detailToText(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail.trim();
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        const loc = Array.isArray(item?.loc)
          ? item.loc.filter((part) => part !== 'body' && part !== 'query').join('.')
          : '';
        const msg = String(item?.msg || item?.message || '').trim();
        return [loc, msg].filter(Boolean).join(': ');
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof detail === 'object') {
    return detailToText(detail.message || detail.detail || '');
  }
  return '';
}

function isUnsafeDetail(text) {
  return /traceback|sqlalchemy|psycopg|asyncpg|site-packages|bearer |token=|password|secret|https?:\/\//i.test(
    text,
  );
}

/** Texto do erro da API, quando dá para mostrar ao usuário. */
export function apiErrorDetail(error) {
  const data = error?.response?.data;
  const text = detailToText(data?.detail ?? data?.message);
  if (!text || isUnsafeDetail(text)) return '';
  const known = KNOWN_DETAIL.find(([pattern]) => pattern.test(text));
  return known ? known[1] : text;
}

/**
 * Maps API/network failures to product i18n keys.
 * Never returns stack traces, URLs, HTTP codes, tenant ids or service names.
 */
export function resolveErrorKey(error, fallbackKey = 'unexpectedError') {
  const fallback = withNs(fallbackKey);
  if (!error) return fallback;

  const status = error.response?.status ?? error.status;
  const code = error.code;
  const raw = String(error.message || error.type || '');
  const detail = error.response?.data?.detail;
  const apiCode =
    (detail && typeof detail === 'object' && detail.code) ||
    (typeof detail === 'string' && /EMAIL_ALREADY_EXISTS|Já existe um usuário com este e-mail/i.test(detail)
      ? 'EMAIL_ALREADY_EXISTS'
      : null);

  const detailText = typeof detail === 'string' ? detail : detail?.message || '';

  if (apiCode === 'EMAIL_ALREADY_EXISTS') return `${COMMON_PREFIX}emailAlreadyExists`;
  if (apiCode === 'USERNAME_ALREADY_EXISTS') return `${COMMON_PREFIX}usernameAlreadyExists`;
  if (/cpf already exists|este cpf/i.test(detailText)) return `${COMMON_PREFIX}cpfAlreadyExists`;
  if (/e-mail já está em uso|email already exists|este e-mail/i.test(detailText)) {
    return `${COMMON_PREFIX}emailAlreadyExists`;
  }
  if (apiCode === 'ACCOUNT_PENDING_ACTIVATION') return `${COMMON_PREFIX}accountPendingActivation`;
  if (apiCode === 'USER_LIMIT_REACHED') return `${COMMON_PREFIX}userLimitReached`;

  if (code === 'ECONNABORTED' || /timeout/i.test(raw)) {
    return `${COMMON_PREFIX}timeoutError`;
  }
  if (
    !error.response &&
    (error.request ||
      code === 'ERR_NETWORK' ||
      /network error|failed to fetch|load failed/i.test(raw))
  ) {
    return `${COMMON_PREFIX}networkError`;
  }

  if (status === 401) return `${COMMON_PREFIX}sessionExpired`;
  if (status === 402) return `${COMMON_PREFIX}billingBlocked`;
  if (status === 403) return `${COMMON_PREFIX}forbiddenError`;
  if (status === 404) return `${COMMON_PREFIX}notFoundError`;
  if (status === 409) return `${COMMON_PREFIX}conflictError`;
  if (status === 422 || status === 400) return `${COMMON_PREFIX}validationError`;
  if (status === 429) return `${COMMON_PREFIX}tooManyRequests`;
  if (status >= 500) return `${COMMON_PREFIX}serverError`;

  return fallback;
}

const GENERIC_KEY = /(?:validation|forbidden|conflict|notFound|server|save|unexpected)Error$/;

export function userFacingError(error, fallbackKey = 'unexpectedError') {
  const key = resolveErrorKey(error, fallbackKey);
  const detail = apiErrorDetail(error);
  if (detail && GENERIC_KEY.test(key)) return detail;
  return i18n.t(key);
}

export default userFacingError;
